import type { FsId } from '@fullstory/recording-api';
import { NormalizedTagName } from '@fullstory/rewriters';
import { uaIsSafari } from '@fullstory/useragent';
import { loadEvent } from '@fullstory/util/promise';
import { isEqual } from 'lodash';
import type { LoadResult } from '../frame_playback_host';
import { DirtyBit } from './dirtybits';
import { FsCSSOMSheet } from './fscssom';
import type { FsDocument } from './fsdocument';
import { FsDocumentOrShadowRoot } from './fsdocumentorshadowroot';
import { FsNode } from './fsnode';

/**
 * Shared behaviors for node types which have an associated CSSOM.
 *
 * This is equivalent to the DOM LinkStyle interface, but with a hopefully
 * clearer name.
 */
export abstract class FsCSSOMElement extends FsNode {
  protected _sheet!: FsCSSOMSheet;
  get sheet() {
    return this._sheet;
  }

  constructor(ownerDoc: FsDocument, tag: NormalizedTagName, fsid?: FsId) {
    super(ownerDoc, tag, undefined, fsid);
    this.resetStyleSheet();
  }

  onSheetChange(): void {
    this.setDirtyBit(DirtyBit.StyleSheet);
  }

  resetStyleSheet(): void {
    this._sheet = new FsCSSOMSheet(this.ownerDoc, [this]);
  }

  protected flushProperties(dom: HTMLLinkElement | HTMLStyleElement): void {
    super.flushProperties(dom);

    // Flush this node's stylesheet. This needs to happen after:
    //  - This node is flushed to the DOM, so that the stylesheet is accessible
    //    via the CSSOM.
    //  - This node's descendants are flushed, because inserting or removing
    //    child text nodes of a <style> element can trigger a stylesheet reset.
    //  - This node's attributes are flushed, because changing a <link>
    //    element's 'href' or 'rel' attribute can trigger a stylesheet reset.
    if (this._hasDirtyBit(DirtyBit.StyleSheet) && !this.isHelper()) {
      this._sheet.flush();
      this._clearDirtyBit(DirtyBit.StyleSheet);
    }
  }

  protected invalidateProperties(): void {
    super.invalidateProperties();
    this.setDirtyBit(DirtyBit.StyleSheet);
  }
}

/** The vDOM representation of an {@link HTMLStyleElement} node. */
export class FsStyleElement extends FsCSSOMElement {
  cssRewritingFailed: boolean = false;

  constructor(ownerDoc: FsDocument, fsid?: FsId) {
    super(ownerDoc, 'style', fsid);
  }

  protected createNode(doc: Document): HTMLStyleElement {
    return doc.createElement('style');
  }

  insert(child: FsNode, before: FsNode | null): void {
    super.insert(child, before);

    // Inserting a descendant will cause the text content of this node to be
    // reprocessed, triggering a stylesheet reset.
    this.resetStyleSheet();
  }

  removeChild(child: FsNode): void {
    super.removeChild(child);

    // As with inserting, removing a descendant triggers a stylesheet reset.
    this.resetStyleSheet();
  }

  private _linkedShadowRoot: FsDocumentOrShadowRoot | undefined = undefined;
  public linkShadowRoot(node: FsDocumentOrShadowRoot) {
    this._linkedShadowRoot = node;
  }

  onSheetChange(): void {
    super.onSheetChange();
    this._linkedShadowRoot?.onSheetChange();
  }
}

/** Metadata about a resource loaded by a <link> element. */
type LinkResource = { href: string; rel: string };

/** The vDOM representation of an {@link HTMLLinkElement} node. */
export class FsLinkElement extends FsCSSOMElement {
  private _linkResource: LinkResource | undefined = undefined;
  private _freezeMediaAttribute: boolean = false;

  constructor(ownerDoc: FsDocument, fsid?: FsId) {
    super(ownerDoc, 'link', fsid);
  }

  protected createNode(doc: Document): HTMLLinkElement {
    const dom = doc.createElement('link');
    dom.addEventListener('load', this.ownerDoc.handlers.onRenderingMayChange);
    dom.addEventListener('error', this.ownerDoc.handlers.onRenderingMayChange);
    return dom;
  }

  protected onAttrChanged(name: string, _value: string | null): void {
    if (name === 'href' || name === 'rel') {
      this.resetStyleSheet();
    }
  }

  protected flushAttr(dom: HTMLLinkElement, name: string, value: string | null): void {
    if (name === 'href' || name === 'rel') {
      this._maybeTriggerLinkLoad(dom, {
        href: this.attr('href'),
        rel: this.attr('rel'),
      });
      return;
    }

    if (name === 'media' && this._freezeMediaAttribute) {
      // On Safari, we need to avoid flushing changes to the 'media' attribute
      // to the DOM while a stylesheet is loading. See _maybeTriggerLinkLoad()
      // for more details.
      return;
    }

    super.flushAttr(dom, name, value);
  }

  /**
   * Set the attributes that will trigger a <link> element to start loading, if
   * we're ready to do so.
   */
  private async _maybeTriggerLinkLoad(
    elem: HTMLLinkElement,
    resource: LinkResource,
  ): Promise<void> {
    if (isEqual(this._linkResource, resource)) {
      return; // Nothing has changed.
    }

    this._linkResource = resource;

    // Without both attributes, we don't know what we're loading. It may be
    // something that we don't allow in playback. It also may be that we never
    // get one of the two attributes, in which case we don't want to add this
    // <link> element to the set of loading resources, since it will never load.
    // TODO(sethf): Should we cancel loads, unset _freezeMediaAttribute, and
    // flush disabled status here?
    if (resource.rel === '' || resource.href === '') {
      elem.removeAttribute('href');
      elem.removeAttribute('rel');
      return;
    }

    let loadResult: LoadResult = 'load';
    let loadOrErrorPromise = (async () => {
      try {
        await loadEvent(elem, () => {
          elem.setAttribute('href', resource.href);
          elem.setAttribute('rel', resource.rel);
        });
      } catch (_) {
        loadResult = 'error';
      }
    })();

    const resolvedLoaded = this.ownerDoc.addLoadingResource(resource.href, 'style');

    if (uaIsSafari) {
      // Workaround for styles blocking rendering on Safari. This can make the
      // whole recording blank when a CSS url times out. To avoid that, we use
      // a media query to disable the stylesheet. For more context, see:
      //   https://bugs.webkit.org/show_bug.cgi?id=88869
      elem.setAttribute('media', 'never-matches');
      this._freezeMediaAttribute = true;
      loadOrErrorPromise = loadOrErrorPromise.then(() => {
        this._freezeMediaAttribute = false;
        if (this.attrs['media']) {
          elem.setAttribute('media', this.attrs['media']);
        } else {
          elem.removeAttribute('media');
        }
      });
    }

    await loadOrErrorPromise;

    requestAnimationFrame(() => {
      // Flush disabled status.
      if (this.sheet) {
        this.sheet.flush();
      }

      this.ownerDoc.handlers.onRenderingMayChange();
      resolvedLoaded(loadResult);
    });
  }
}
